import React, {
    useState,
    useCallback,
    useContext,
    useLayoutEffect,
} from 'react';
import { useHistory } from 'react-router-dom';
import { FordRidesHome, AllRides } from '../../components/sections/ford-rides';
import FordRidesContextProvider, {
    ReducerInitialStates,
} from '../../contexts/ford-rides-context/ford-rides-context';
import { useVehiclePageContent } from '../../components/sections/vehicle-portal/hooks/use-vehicle-page-content';
import { GA_ZERO_CAR_SCENARIO_COUNTRIES } from '../../constants';
import serverContext from '../../contexts/serverContext';
import useUserProfile from '../../hooks/use-user-profile';
import AuthenticationService from '../../services/authentication-service/authentication-service';
import { goToMyAccount } from '../vehicle-dashboard-view/vehicle-dashboard-view';
import ProfileService from '../../services/profile-service/profile-service';
import VehicleTypeService from '../../services/vehicle-type-service/vehicle-type-service';
import { ActivityIndicator } from '../../components/common/activity-indicator/activity-indicator';
import { DisclosuresLink } from '../../components/sections/disclosures-link/disclosures-link';
import { Breadcrumbs } from '../../components/sections';
import { useFordRidesContent } from '../../components/sections/ford-rides/hooks/use-ford-rides-content';
import { StringUtil } from '../../components/utils/string-util/string-util';
import './ford-rides-view.scss';
import {
    StorageType,
    WebStorage,
} from '../../components/sections/ford-rides/utils/web-storage';
import { getVinFromLocalStorageIfExists } from '../../components/sections/pre-imagine-components/pre-imagine-vehicle-selector/pre-imagine-vin-selector/pre-imagine-vin-selector';
import useSeoConfig from '../../hooks/use-seo-config';

export enum ComponentType {
    FordRidesHome = 'FordRidesHome',
    AllRides = 'AllRides',
}

const components = {
    [ComponentType.FordRidesHome]: FordRidesHome,
    [ComponentType.AllRides]: AllRides,
};

export type RideType = keyof ReducerInitialStates;

export enum VehicleStatus {
    loading,
    connectedVin,
    notConnectedVin,
}

interface UserPreferences {
    [key: string]: {
        lastSelectedVin: string;
    };
}

function getVinFromWebStorageFordRides() {
    let queryVin = null;
    const userPreferences: UserPreferences = new WebStorage(
        'userPreferences',
        StorageType.local
    ).storageValue as UserPreferences;
    const userPreferencesVin =
        userPreferences &&
        Object.values(userPreferences).find(value => value.lastSelectedVin)
            ?.lastSelectedVin;
    const selectedVin = new WebStorage('selectedVin', StorageType.session)
        .storageValue as string;
    queryVin = selectedVin || userPreferencesVin;
    return queryVin || getVinFromLocalStorageIfExists();
}

export function FordRidesView() {
    useSeoConfig('ford-rides', 'ford-rides');
    const [vin, setVin] = useState(getVinFromWebStorageFordRides());
    const history = useHistory();
    const fordRidesData = useFordRidesContent();
    const [vehicleStatus, setVehicleStatus] = useState<VehicleStatus>(
        VehicleStatus.loading
    );
    const profileService = new ProfileService();
    const authenticationService = new AuthenticationService();
    const goToVehicleDashboard = useCallback(
        (vehicleStatus?: VehicleStatus) => {
            history?.push('/vehicle-dashboard', {
                vehicleStatus,
            });
        },
        [history]
    );
    const profile = useUserProfile();
    const vehiclePageContent = useVehiclePageContent();
    const {
        currentRegionCode,
        currentLanguageRegionCode,
        current3LetterCountryCode,
    } = useContext(serverContext);

    useLayoutEffect(() => {
        if (profile) {
            if (profile.vehicles.length > 0) {
                if (vin) {
                    return;
                }
                const [match] = [profile.vehicles[0]];
                const selectedVin = profileService.getUserPreferences(
                    profile.profile.email
                )?.lastSelectedVin;
                selectedVin ? setVin(selectedVin) : setVin(match.vin);
            } else {
                GA_ZERO_CAR_SCENARIO_COUNTRIES.includes(currentRegionCode)
                    ? goToMyAccount(vehiclePageContent)
                    : goToVehicleDashboard();
            }
        } else {
            authenticationService.onIsAuthenticated().then(authenticated => {
                !authenticated && authenticationService.login();
            });
        }
    }, [profile, goToVehicleDashboard]);

    useLayoutEffect(() => {
        const getVinVehicleResponse = () => {
            new VehicleTypeService()
                .request(
                    vin,
                    currentLanguageRegionCode,
                    current3LetterCountryCode?.toUpperCase()
                )
                .then(response => {
                    if (response.isConnected) {
                        setVehicleStatus(VehicleStatus.connectedVin);
                    } else {
                        setVehicleStatus(VehicleStatus.notConnectedVin);
                        goToVehicleDashboard(VehicleStatus.notConnectedVin);
                    }
                })
                .catch(() => {
                    goToVehicleDashboard();
                });
        };
        process.env.REACT_APP_ACTIVE_PROFILE &&
        ['prod'].includes(process.env.REACT_APP_ACTIVE_PROFILE)
            ? vin && getVinVehicleResponse()
            : vin && setVehicleStatus(VehicleStatus.connectedVin);
    }, [vin, goToVehicleDashboard]);

    const [view, setView] = useState<ComponentType>(
        ComponentType.FordRidesHome
    );

    const [rideType, setRideType] = useState<RideType[]>([]);

    const Component = components[view];

    if (
        VehicleStatus.loading === vehicleStatus ||
        !Object.keys(fordRidesData).length
    ) {
        return <ActivityIndicator className="full-height" />;
    }

    return (
        <>
            <section className="ford-rides__wrapper">
                <Breadcrumbs
                    currentPage={
                        view === ComponentType.FordRidesHome
                            ? StringUtil.sentenceCaseWithoutSanitize(
                                  fordRidesData.fordRidesHeading
                              )
                            : StringUtil.sentenceCaseWithoutSanitize(
                                  fordRidesData.allRidesHeading
                              )
                    }
                >
                    {view === ComponentType.AllRides ? (
                        <>
                            <a
                                className="breadcrumb-link"
                                href="#"
                                onClick={(
                                    e: React.MouseEvent<HTMLAnchorElement>
                                ) => {
                                    e.preventDefault();
                                    setView(ComponentType.FordRidesHome);
                                }}
                            >
                                {StringUtil.sentenceCaseWithoutSanitize(
                                    fordRidesData.fordRidesHeading
                                )}
                            </a>{' '}
                            <span className="back-slash">{`>`}</span>
                        </>
                    ) : (
                        <></>
                    )}
                </Breadcrumbs>
                {vehicleStatus === VehicleStatus.connectedVin ? (
                    <FordRidesContextProvider
                        vin={vin}
                        fordRidesData={fordRidesData}
                    >
                        <Component
                            fordRidesData={fordRidesData}
                            rideType={rideType}
                            setRideType={setRideType}
                            setView={setView}
                        />
                    </FordRidesContextProvider>
                ) : null}
            </section>
            <DisclosuresLink
                class="cx-disclosure-content"
                disclosuresType="ford-rides"
            />
        </>
    );
}

export default FordRidesView;
